<template>
  <article class="js-product-card ProductCard" :class="[determineLayout]" ref="card" data-ht-component="collection-content-tile" v-if="loaded">
    <div class="ProductCard-ImageWrapper" :class="loadedMedia">
      <component :is="conditionalElement" v-bind="conditionalProps">
        <template v-if="isVideo">
          <video class="ProductCard__Video ProductCard__Video--Desktop" @loadeddata="transitionMedia" loop autoplay playsinline muted preload="none" ref="desktopVideo" :poster="mediaObject('desktop_media')?.previewImage?.url">
            <source v-for="src in mediaObject('desktop_media').sources" :key="src" :src="src.url" :type="src.mimeType" />
          </video>
          <video class="ProductCard__Video ProductCard__Video--Mobile" @loadeddata="transitionMedia" loop autoplay playsinline muted preload="none" ref="mobileVideo" :poster="mediaObject('mobile_media')?.previewImage?.url">
            <source v-for="src in mediaObject('mobile_media').sources" :key="src" :src="src.url" :type="src.mimeType" />
          </video>
        </template>
        <template v-else-if="isImage">
          <picture class="ProductCard__Image">
            <source media="(max-width:1024px)" :srcset="mediaObject('mobile_media')?.image?.url" />
            <source media="(min-width:1025px)" :srcset="mediaObject('desktop_media')?.image?.url" />
            <img loading="lazy" @load="transitionMedia" :src="mediaObject('mobile_media')?.image?.url" :alt="mediaObject('desktop_media')?.altText || ''" />
          </picture>
        </template>
      </component>
      <ContentFrame :element="conditionalElement" :properties="conditionalProps" :playing="isPlaying" :content="contentModal" @handlePlayClick="handlePlayClick" />
    </div>
    <component v-if="hasCaption" class="p5" data-color="night-sky-dark" :is="conditionalElement" v-bind="conditionalProps" v-text="contentModal.caption.value" />
  </article>
</template>

<script setup>
// Vue core
import { ref, onMounted, computed, watch } from 'vue';

// Composables
import { CONTENT_CARD_QUERY } from './Composables/Queries';

// Utilities
import { fetchStorefront } from '@utils/GraphQLClient';
import ContentFrame from './ContentFrame.vue';

const card = ref(null);
const data = ref(false);
const inView = ref(false);
const loaded = ref(false);
const observer = ref(null);
const isPlaying = ref(true);
const mobileVideo = ref(null);
const desktopVideo = ref(null);

// Props
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  selectionListener: {
    type: Boolean,
    default: false,
  },
  ready: {
    type: Boolean,
    default: false,
  },
  appliedFilters: {
    type: Array,
    required: false,
    default: [],
  },
  terms: {
    type: Object,
    required: false,
    default: {},
  },
});

const determineLayout = computed(() => {
  const layout = contentModal?.value?.layout?.value || 'Single';

  switch (layout) {
    case 'Wide':
      return 'ProductCard--Content ProductCard--Wide';
    default:
      return 'ProductCard--Content';
  }
});

const conditionalElement = computed(() => {
  const content = data.value?.contentModal || {};
  const url = content?.url?.value;
  const hasUrl = typeof url === 'string' && url.trim() !== '';

  if (hasUrl) {
    return 'a';
  } else {
    return 'div';
  }
});

const conditionalProps = computed(() => {
  const isLink = conditionalElement.value === 'a';
  if (!isLink) return {};
  const url = data.value?.contentModal?.url?.value || '';
  const isShopTheLook = url.includes('stl-product');
  const element = isShopTheLook ? 'shop-the-look-content-tile' : 'content-tile-cta';

  return {
    href: url,
    ['data-ht-element']: element,
  };
});

const contentModal = computed(() => data.value?.contentModal || {});

const isVideo = computed(() => {
  const content = contentModal.value;
  return content?.desktop_media?.reference?.__typename === 'Video' || content?.mobile_media?.reference?.__typename === 'Video';
});

const isImage = computed(() => {
  const content = contentModal.value;
  return content?.desktop_media?.reference?.__typename === 'MediaImage' || content?.mobile_media?.reference?.__typename === 'MediaImage';
});

const mediaObject = computed(() => {
  return (instance) => {
    const video = contentModal.value[instance]?.reference;
    return video;
  };
});

const hasCaption = computed(() => {
  const content = contentModal.value;
  return content?.caption?.value && content?.caption?.value.trim() !== '';
});

const formatContentModal = (content) => {
  const fields = content?.reference?.fields || [];
  if (!fields || fields.length === 0) return {};

  const response = fields.reduce((data, { key, ...item }) => {
    data[key] = item;
    return data;
  }, {});

  return response;
};

const fetchContent = async () => {
  const request = {
    query: CONTENT_CARD_QUERY,
    variables: {
      handle: props.item.handle,
    },
  };

  try {
    const { data: response } = await fetchStorefront(request);
    data.value = response.product;
    data.value.contentModal = formatContentModal(data.value.contentModal);
    loaded.value = true;
  } catch (error) {
    loaded.value = false;
    data.value = false;
    console.error('Error fetching content:', error);
  }
};

const loadedMedia = computed(() => (inView.value && loaded.value ? 'ProductCard-ImageWrapper--Loaded' : ''));
const transitionMedia = () => (loaded.value = true);
const handlePlayClick = (value) => (isPlaying.value = value);

const unobserve = () => {
  if (observer.value) observer.value.disconnect();
};

const initializeObserver = () => {
  unobserve();
  observer.value = new IntersectionObserver(
    (entries) => {
      const { isIntersecting } = entries[0];
      if (isIntersecting) {
        inView.value = true;
        unobserve();
      }
    },
    { threshold: 0.6 }
  );
};

const observeCard = () => {
  unobserve();
  observer.value.observe(card.value);
};

watch(
  () => isPlaying.value,
  (playing) => {
    const videos = [desktopVideo.value, mobileVideo.value];
    videos.forEach((video) => (playing ? video.play() : video.pause()));
  }
);

onMounted(async () => {
  await fetchContent();
  initializeObserver();
  observeCard();
});
</script>
